import * as React from 'react'
import { BadgeCheck, Loader2, Plus } from 'lucide-react'
import type { RecognizedAppDto, SeenAppDto } from '@lib/dto/mcp.dto'
import { recognizedAppMatchSchema, type RecognizedAppMatch } from '@lib/enums/recognized-app-match.enum'
import { addRecognizedAppInputSchema, type AddRecognizedAppInput } from '@lib/input/mcp.input'
import { validateRecognizedAppValue } from '@lib/validators/recognized-app.validator'
import { useAuth } from '@/auth/AuthContext'
import { EndAccessDialog } from '@/components/mcp/ConnectionParts'
import { PageHeader } from '@/components/PageHeader'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage, apiFieldErrors } from '@/helpers/apiErrorMessage'
import { useMcpEnabled, useMcpStatus, useOpsMcpActions, useRecognizedApps, useSeenApps } from '@/hooks/useConnectedApps'

const MATCH_LABELS: Record<RecognizedAppMatch, string> = {
  redirect_uri: 'Redirect URI',
  client_id_url: 'Client ID metadata URL',
}

const MATCH_HINTS: Record<RecognizedAppMatch, string> = {
  redirect_uri: 'The exact address the app returns to after signing in, e.g. https://claude.ai/api/mcp/auth_callback.',
  client_id_url: 'The https address of the app’s client ID metadata document.',
}

const FIELDS = ['name', 'match', 'value'] as const

type FieldErrors = Partial<Record<(typeof FIELDS)[number], string>>

/** Checks a new recognized app the way the API will: the shared input schema, then the value's rules. */
function validate(raw: { name: string; match: RecognizedAppMatch; value: string }): { input: AddRecognizedAppInput } | { errors: FieldErrors } {
  const parsed = addRecognizedAppInputSchema.safeParse(raw)
  if (!parsed.success) {
    const errors: FieldErrors = {}
    for (const issue of parsed.error.issues) {
      const field = FIELDS.find((name) => name === issue.path[0])
      if (field && !errors[field]) errors[field] = field === 'name' ? 'Give the app a name.' : 'Enter a full URL.'
    }
    return { errors }
  }
  const problem = validateRecognizedAppValue(parsed.data.match, parsed.data.value)
  return problem ? { errors: { value: problem } } : { input: parsed.data }
}

/** What the add dialog starts with: empty, or an app people signed in with. */
interface AppDraft {
  name: string
  match: RecognizedAppMatch
  value: string
}

const EMPTY_DRAFT: AppDraft = { name: '', match: 'redirect_uri', value: '' }

interface AddAppDialogProps {
  open: boolean
  initial: AppDraft
  onClose: () => void
}

/** Recognizing an AI app: connections from it can then be granted live changes and staff changes. */
function AddAppDialog({ open, initial, onClose }: AddAppDialogProps) {
  const { addRecognizedApp } = useOpsMcpActions()
  const [name, setName] = React.useState(initial.name)
  const [match, setMatch] = React.useState<RecognizedAppMatch>(initial.match)
  const [value, setValue] = React.useState(initial.value)
  const [errors, setErrors] = React.useState<FieldErrors>({})
  const [error, setError] = React.useState<string | null>(null)

  function close(): void {
    setName(initial.name)
    setMatch(initial.match)
    setValue(initial.value)
    setErrors({})
    setError(null)
    onClose()
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const checked = validate({ name, match, value })
    if ('errors' in checked) {
      setErrors(checked.errors)
      return
    }
    setErrors({})
    setError(null)
    try {
      const app = await addRecognizedApp.mutateAsync(checked.input)
      toast.success(`Recognized ${app.name}`)
      close()
    } catch (failure) {
      const fieldErrors = apiFieldErrors(failure, FIELDS)
      if (fieldErrors) setErrors(fieldErrors)
      else setError(apiErrorMessage(failure, 'Could not add the app.'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? null : close())}>
      <DialogContent>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>Recognize an AI app</DialogTitle>
            <DialogDescription>
              People can then give connections from it live changes, and staff can give it staff changes. Recognize only apps you trust.
            </DialogDescription>
          </DialogHeader>
          <Field label="Name" htmlFor="app-name" error={errors.name ?? null}>
            <Input
              id="app-name"
              autoComplete="off"
              value={name}
              aria-invalid={errors.name ? true : undefined}
              aria-describedby={fieldMessageId('app-name')}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
          <Field label="Match" htmlFor="app-match" error={errors.match ?? null}>
            <Select
              id="app-match"
              value={match}
              onChange={(event) => {
                const parsed = recognizedAppMatchSchema.safeParse(event.target.value)
                if (parsed.success) setMatch(parsed.data)
              }}
            >
              {recognizedAppMatchSchema.options.map((option) => (
                <option key={option} value={option}>
                  {MATCH_LABELS[option]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Value" htmlFor="app-value" hint={MATCH_HINTS[match]} error={errors.value ?? null}>
            <Input
              id="app-value"
              className="font-mono"
              autoComplete="off"
              spellCheck={false}
              placeholder="https://"
              value={value}
              aria-invalid={errors.value ? true : undefined}
              aria-describedby={fieldMessageId('app-value')}
              onChange={(event) => setValue(event.target.value)}
            />
          </Field>
          {error ? <ErrorState message={error} /> : null}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={addRecognizedApp.isPending}>
              {addRecognizedApp.isPending ? <Loader2 className="animate-spin" /> : null}
              Add app
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface AppsTableProps {
  apps: RecognizedAppDto[]
  canRemove: boolean
  onRemove: (app: RecognizedAppDto) => void
}

function AppsTable({ apps, canRemove, onRemove }: AppsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Matches</TableHead>
          <TableHead>Added by</TableHead>
          <TableHead>Added</TableHead>
          <TableHead className="text-right">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {apps.map((app) => (
          <TableRow key={app.id}>
            <TableCell className="font-medium">{app.name}</TableCell>
            <TableCell>
              <div className="text-caption text-muted-foreground">{MATCH_LABELS[app.match]}</div>
              <div className="font-mono text-caption break-all">{app.value}</div>
            </TableCell>
            <TableCell>{app.addedBy ?? '—'}</TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground">{new Date(app.createdAt).toLocaleDateString()}</TableCell>
            <TableCell className="text-right">
              {canRemove ? (
                <Button variant="destructive-quiet" size="sm" onClick={() => onRemove(app)}>
                  Remove
                </Button>
              ) : null}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/** The first way an app identifies itself that can go on the list (its client ID URL when it has one), or null. */
function recognizableDraft(app: SeenAppDto): AppDraft | null {
  const identity = app.identities.find((item) => item.problem === null)
  return identity ? { name: app.name, match: identity.match, value: identity.value } : null
}

interface SeenAppsTableProps {
  apps: SeenAppDto[]
  canRecognize: boolean
  onRecognize: (draft: AppDraft) => void
}

function SeenAppsTable({ apps, canRecognize, onRecognize }: SeenAppsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>App</TableHead>
          <TableHead>Signs in through</TableHead>
          <TableHead className="text-right">Connections</TableHead>
          <TableHead>Last used</TableHead>
          <TableHead className="text-right">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {apps.map((app) => {
          const draft = recognizableDraft(app)
          return (
            <TableRow key={app.clientId}>
              <TableCell>
                <div className="font-medium">{app.name}</div>
                {app.recognizedAs ? <Badge tone="success">Recognized as {app.recognizedAs}</Badge> : <Badge tone="warning">Not recognized</Badge>}
              </TableCell>
              <TableCell className="space-y-1">
                {app.identities.map((identity) => (
                  <div key={`${identity.match}:${identity.value}`}>
                    <div className="text-caption text-muted-foreground">{MATCH_LABELS[identity.match]}</div>
                    <div className="font-mono text-caption break-all">{identity.value}</div>
                    {identity.problem ? <div className="text-caption text-muted-foreground">{identity.problem}</div> : null}
                  </div>
                ))}
              </TableCell>
              <TableCell className="text-right tabular-nums">{app.activeConnections}</TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">{new Date(app.lastUsedAt).toLocaleString()}</TableCell>
              <TableCell className="text-right">
                {canRecognize && !app.recognizedAs && draft ? (
                  <Button variant="secondary" size="sm" onClick={() => onRecognize(draft)}>
                    Recognize
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

/**
 * The AI apps PixelSign recognizes. Only connections from these can be
 * granted live changes or staff changes; others get test and live read-only
 * at most. Admins add and remove them; Support staff see the list.
 */
export function OpsRecognizedAppsPage() {
  const { user } = useAuth()
  const status = useMcpStatus()
  const enabled = useMcpEnabled()
  const query = useRecognizedApps({ enabled })
  const seen = useSeenApps({ enabled })
  const { removeRecognizedApp } = useOpsMcpActions()
  const [adding, setAdding] = React.useState<AppDraft | null>(null)
  const [removing, setRemoving] = React.useState<RecognizedAppDto | null>(null)
  const isAdmin = user?.role === 'ADMIN'

  function renderBody() {
    if (status.isLoading) return <LoadingState />
    if (!enabled) {
      return <EmptyState icon={BadgeCheck} title="Not available yet" description="Connecting AI apps to PixelSign isn’t switched on yet." />
    }
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) return <ErrorState message={apiErrorMessage(query.error, 'Could not load the recognized apps.')} />
    if (query.data.apps.length === 0) {
      return (
        <EmptyState
          icon={BadgeCheck}
          title="No recognized apps"
          description="Until an app is recognized, its connections get test and live read-only access at most."
        />
      )
    }
    return <AppsTable apps={query.data.apps} canRemove={isAdmin} onRemove={setRemoving} />
  }

  function renderSeen() {
    if (seen.isLoading) return <LoadingState />
    if (seen.isError || !seen.data) return <ErrorState message={apiErrorMessage(seen.error, 'Could not load the apps people signed in with.')} />
    if (seen.data.apps.length === 0) {
      return <EmptyState icon={BadgeCheck} title="No sign-ins yet" description="Apps appear here once someone signs in with them." />
    }
    return <SeenAppsTable apps={seen.data.apps} canRecognize={isAdmin} onRecognize={setAdding} />
  }

  return (
    <>
      <PageHeader
        title="Recognized apps"
        description="AI apps whose connections can be given live changes and staff changes. Other apps get test and live read-only access at most."
        actions={
          isAdmin && enabled ? (
            <Button onClick={() => setAdding(EMPTY_DRAFT)}>
              <Plus />
              Add app
            </Button>
          ) : null
        }
      />
      {enabled ? (
        <div className="space-y-8">
          <section className="space-y-3">
            <h2 className="text-title font-semibold">Recognized</h2>
            {renderBody()}
          </section>
          <section className="space-y-3">
            <div className="space-y-1">
              <h2 className="text-title font-semibold">Apps people signed in with</h2>
              <p className="text-caption text-muted-foreground">
                The last 90 days. Any app can call itself anything, so go by how it signs in: recognize only addresses you know belong to the app.
              </p>
            </div>
            {renderSeen()}
          </section>
        </div>
      ) : (
        renderBody()
      )}
      {adding ? <AddAppDialog key={`${adding.match}:${adding.value}`} open initial={adding} onClose={() => setAdding(null)} /> : null}
      <EndAccessDialog
        open={removing !== null}
        title={`Stop recognizing ${removing?.name ?? 'this app'}?`}
        description={`Connections from ${removing?.name ?? 'it'} keep working, but drop to live read-only and lose staff changes on their next call.`}
        confirmLabel="Remove"
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return
          await removeRecognizedApp.mutateAsync(removing.id)
          toast.success(`Stopped recognizing ${removing.name}`)
        }}
      />
    </>
  )
}
